import React from 'react';
import { Music, Award, Heart, Users, Star, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { aboutInfo, portfolioItems } from '../data/mock';

export const About = () => {
  const profileImage = portfolioItems.find(item => item.category === 'about-profile');

  const achievements = [
    {
      icon: Award,
      title: "Expertise Professionnelle",
      description: "Formation musicale classique et expérience DJ depuis plus de 10 ans"
    },
    {
      icon: Heart,
      title: "Passion & Dévotion",
      description: "Chaque événement est unique et mérite une attention particulière"
    },
    {
      icon: Users,
      title: "Approche Personnalisée",
      description: "Écoute attentive de vos souhaits pour créer l'ambiance parfaite"
    },
    {
      icon: Star,
      title: "Excellence & Qualité",
      description: "Matériel professionnel de pointe et prestations irréprochables"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-ivory via-pearl to-light-gold/20 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-charcoal mb-6 leading-tight">
                {aboutInfo.title}
              </h1>
              <p className="text-xl text-warm-gray mb-8 leading-relaxed">
                {aboutInfo.description}
              </p>
              
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="text-center p-4 bg-white/60 backdrop-blur-sm rounded-lg">
                  <div className="text-2xl font-bold text-gold mb-1">10+</div>
                  <div className="text-sm text-charcoal">Années d'expérience</div>
                </div>
                <div className="text-center p-4 bg-white/60 backdrop-blur-sm rounded-lg">
                  <div className="text-2xl font-bold text-gold mb-1">50+</div>
                  <div className="text-sm text-charcoal">Mariages premium</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl p-8 backdrop-blur-sm border border-white/50">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg">
                  {profileImage && profileImage.image_url ? (
                    <img 
                      src={profileImage.image_url} 
                      alt="Photo de profil" 
                      className="w-32 h-32 rounded-full mx-auto mb-4 object-cover shadow-lg"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-24 h-24 bg-gradient-to-r from-blue-900 to-blue-700 rounded-full mb-4 mx-auto">
                      <Music className="h-12 w-12 text-white" />
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-charcoal text-center mb-4">
                    Mon engagement
                  </h3>
                  <p className="text-warm-gray text-center leading-relaxed">
                    Transformer vos moments précieux en souvenirs inoubliables grâce à une musique d'exception et une prestation sur mesure.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Mon Expertise
            </h2>
            <p className="text-xl text-warm-gray max-w-2xl mx-auto">
              Une combinaison unique de compétences musicales et techniques
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <Card key={index} className="border-0 bg-gradient-to-br from-ivory/50 to-pearl/50 hover:shadow-lg transition-all duration-300 group">
                <CardContent className="p-6 text-center">
                  <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-4 rounded-full w-16 h-16 mx-auto mb-4 group-hover:shadow-lg transition-all duration-300">
                    <achievement.icon className="h-8 w-8 text-white mx-auto" />
                  </div>
                  <h3 className="text-lg font-bold text-charcoal mb-3 group-hover:text-blue-900 transition-colors">
                    {achievement.title}
                  </h3>
                  <p className="text-warm-gray text-sm leading-relaxed">
                    {achievement.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-20 bg-gradient-to-br from-ivory/30 to-pearl/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-6">
                Mes Compétences
              </h2>
              <div className="space-y-4">
                {aboutInfo.expertise.map((skill, index) => (
                  <div key={index} className="flex items-center space-x-4 p-4 bg-white/60 backdrop-blur-sm rounded-lg hover:shadow-md transition-all duration-300">
                    <div className="w-2 h-2 bg-gold rounded-full flex-shrink-0"></div>
                    <span className="text-charcoal font-medium">{skill}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-3 rounded-lg">
                      <Music className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-charcoal">Saxophone</h3>
                      <p className="text-sm text-warm-gray">Jazz, Pop, Lounge, Electro</p>
                    </div>
                  </div>
                  <p className="text-warm-gray text-sm leading-relaxed">
                    Répertoire varié adapté à chaque moment de votre événement, 
                    du jazz moderne à l'electro pour créer l'ambiance parfaite.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-3 rounded-lg">
                      <Clock className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-charcoal">Animation DJ</h3>
                      <p className="text-sm text-warm-gray">Généraliste mariage & spécialisé électronique</p>
                    </div>
                  </div>
                  <p className="text-warm-gray text-sm leading-relaxed">
                    DJ généraliste pour mariages avec spécialisation électronique 
                    (BigRoom, Trance, EDM) pour créer l'ambiance parfaite selon vos souhaits.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-8">
            Ma Philosophie
          </h2>
          <div className="bg-gradient-to-br from-ivory to-pearl p-8 md:p-12 rounded-2xl shadow-lg">
            <blockquote className="text-xl md:text-2xl text-charcoal font-light italic leading-relaxed mb-6">
              "Chaque mariage est une histoire unique qui mérite une bande sonore sur mesure. 
              Mon rôle est de créer cette alchimie parfaite entre émotion et célébration, 
              entre intimité et fête, pour que votre jour J résonne encore longtemps dans vos cœurs."
            </blockquote>
            <div className="flex items-center justify-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-900 to-blue-700 rounded-full flex items-center justify-center">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-charcoal">Sax&DJ</p>
                <p className="text-sm text-warm-gray">Créateur d'émotions musicales</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
